import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import { resolveImg } from "../lib/img";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const img = (p) => `${BACKEND}/api/static/img/${p}`;

// 5 cinematic hero slides + dynamic magazine slides appended at runtime.
const STATIC_SLIDES = [
    {
        id: "ouverture",
        kicker: "Vol. I · Ouverture",
        chapter: "01",
        image: img("hero_cliffs_cinematic.jpg"),
        title: ["Une côte, ", { em: "deux" }],
        title2: [{ em: "régions" }, ", un même regard."],
        lead: "De la Côte d'Albâtre à la Baie de Somme, 80 km où la Haute-Normandie et la Picardie partagent les mêmes falaises et la même élégance discrète.",
        cta: { to: "/manifeste", label: "Lire le manifeste" },
        align: "left",
    },
    {
        id: "villa-secret",
        kicker: "Reportage · Villas",
        chapter: "02",
        image: img("hero_villa_secret.jpg"),
        title: ["Les villas ", { em: "Belle" }],
        title2: [{ em: "Époque" }, " de Mers-les-Bains."],
        lead: "Six cents demeures polychromes entre Ault et Le Tréport, dessinées en 1872 par Edmond Lejeune. Visites guidées par l'Office de Tourisme — tous les étés.",
        cta: { to: "/destinations", label: "Entrer dans le territoire" },
        align: "right",
    },
    {
        id: "cliffs",
        kicker: "Paysage · Côte d'Albâtre",
        chapter: "03",
        image: img("hero_cliffs.jpg"),
        title: ["La côte ", { em: "de craie" }, ","],
        title2: ["sur ", { em: "80 kilomètres" }, "."],
        lead: "Une longue muraille blanche qui tombe dans la Manche, de Cayeux-sur-Mer à Berneval-le-Grand. La lumière y change toutes les heures.",
        cta: { to: "/carte", label: "Voir la carte" },
        align: "left",
    },
    {
        id: "foret-mist",
        kicker: "Nature · Forêt d'Eu",
        chapter: "04",
        image: img("real/foret_eu.jpg"),
        title: ["La forêt ", { em: "royale" }],
        title2: ["aux neuf mille ", { em: "hêtres" }, "."],
        lead: "9 300 hectares domaniaux, hêtraie cathédrale, étangs médiévaux, chênes de trois siècles. Accès libre par l'ONF.",
        cta: { to: "/balades", label: "Nos balades en forêt" },
        align: "right",
    },
    {
        id: "brocante",
        kicker: "Rituels · Dimanches",
        chapter: "05",
        image: img("hero_brocante_dawn.jpg"),
        title: ["Chiner, à ", { em: "5h30" }, ","],
        title2: ["c'est un ", { em: "sport" }, "."],
        lead: "La brocante de Blangy, le premier dimanche du mois, ouvre à 5h30. Les chineurs avertis y montent avant le lever du jour.",
        cta: { to: "/parcours/brocantes-dominicales", label: "Le circuit des dimanches" },
        align: "left",
    },
];

const renderTitle = (parts) => parts.map((p, i) => (
    typeof p === "string" ? <span key={i}>{p}</span> :
    <em key={i} className="cp-display-italic text-cp-sable">{p.em}</em>
));

export default function HeroCarousel() {
    const [slides, setSlides] = useState(STATIC_SLIDES);

    // Fetch featured magazine articles and append as carousel slides
    useEffect(() => {
        let alive = true;
        api.get("/articles?featured=true&limit=10").then((r) => {
            if (!alive || !Array.isArray(r.data)) return;
            const articleSlides = r.data.slice(0, 10).map((a, i) => ({
                id: `mag-${a.slug}`,
                kicker: `Magazine · Numéro I`,
                chapter: String(STATIC_SLIDES.length + i + 1).padStart(2, "0"),
                image: resolveImg(a.cover_image) || img("hero_villa_secret.jpg"),
                title: [(a.title || "").split(" ").slice(0, Math.ceil((a.title || "").split(" ").length / 2)).join(" ") + ""],
                title2: [{ em: (a.title || "").split(" ").slice(Math.ceil((a.title || "").split(" ").length / 2)).join(" ") }],
                lead: a.excerpt || a.subtitle || "Un article du magazine Côte Perdue.",
                cta: { to: `/magazine/${a.slug}`, label: "Lire l'article" },
                align: i % 2 === 0 ? "left" : "right",
            }));
            setSlides([...STATIC_SLIDES, ...articleSlides]);
        }).catch(() => {});
        return () => { alive = false; };
    }, []);

    const SLIDES = slides;
    const autoplay = useRef(
        Autoplay({ delay: 8000, stopOnInteraction: false, stopOnMouseEnter: true })
    );
    const [emblaRef, emblaApi] = useEmblaCarousel(
        {
            loop: true,
            duration: 42,
            dragFree: false,
            align: "start",
            containScroll: "trimSnaps",
        },
        [autoplay.current]
    );
    const pauseAutoplay = useCallback(() => autoplay.current?.stop?.(), []);
    const resumeAutoplay = useCallback(() => autoplay.current?.play?.(), []);
    const [selected, setSelected] = useState(0);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelected(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on("select", onSelect);
        emblaApi.on("reInit", onSelect);
    }, [emblaApi, onSelect]);

    const goTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);
    const prev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const next = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

    useEffect(() => {
        const h = (e) => {
            if (e.key === "ArrowLeft") prev();
            else if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [prev, next]);

    const current = SLIDES[selected];

    return (
        <section
            className="relative h-screen w-screen max-w-[100vw] overflow-hidden isolate bg-cp-encre text-cp-ivoire cp-grain"
            data-testid="hero-carousel"
            onMouseEnter={pauseAutoplay}
            onMouseLeave={resumeAutoplay}
            style={{ contain: "paint layout" }}
        >
            {/* Embla viewport: overflow-hidden contains the horizontal flex; w-full avoids mobile overflow */}
            <div className="embla absolute inset-0 w-full h-full overflow-hidden" ref={emblaRef}>
                <div className="embla__container flex h-full w-full">
                    {SLIDES.map((s) => (
                        <div
                            className="embla__slide relative flex-[0_0_100%] w-full min-w-0 max-w-full h-full overflow-hidden"
                            key={s.id}
                        >
                            <img
                                src={s.image}
                                alt=""
                                className="absolute inset-0 w-full h-full object-cover cp-kenburns-slow cp-photo-cinematic"
                                draggable={false}
                            />
                            <div className="absolute inset-0 cp-hero-overlay" />
                        </div>
                    ))}
                </div>
            </div>

            {/* Fixed overlay — animates per slide, stays crisp */}
            <div className="absolute inset-0 pointer-events-none z-10">
                <div className="max-w-[1600px] mx-auto h-full px-5 sm:px-6 md:px-12 pt-28 md:pt-36 pb-24 flex flex-col">
                    {/* Top strip */}
                    <div className="flex items-start justify-between w-full pointer-events-auto gap-4">
                        <div className="flex items-center gap-3 md:gap-5 min-w-0">
                            <span className="cp-serif text-5xl md:text-7xl italic text-cp-sable/80 leading-none shrink-0">{current.chapter}</span>
                            <div className="flex flex-col min-w-0">
                                <span className="cp-tiny text-cp-sable truncate">{current.kicker}</span>
                                <span className="cp-tiny text-cp-ivoire/50 mt-1">
                                    {selected + 1} / {SLIDES.length}
                                </span>
                            </div>
                        </div>
                        <div className="hidden 2xl:flex flex-col items-end gap-2 text-cp-ivoire/60 shrink-0">
                            <span className="cp-tiny">Côte Perdue · N°01</span>
                            <span className="cp-tiny">La côte qu'on se cache</span>
                        </div>
                    </div>

                    {/* Main title */}
                    <div className={`flex-1 flex flex-col ${current.align === "right" ? "md:items-end md:text-right" : "items-start text-left"} justify-end pointer-events-auto`}>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={current.id}
                                initial={{ y: 24, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: -16, opacity: 0 }}
                                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                className="max-w-full md:max-w-3xl lg:max-w-4xl"
                            >
                                <h1 className="cp-serif cp-hero-text break-words text-[36px] xs:text-[42px] sm:text-[54px] md:text-[64px] lg:text-[72px] xl:text-[92px] 2xl:text-[112px] leading-[0.98] md:leading-[0.94] tracking-[-0.02em] md:tracking-[-0.03em]">
                                    {renderTitle(current.title)}<br />
                                    {renderTitle(current.title2)}
                                </h1>
                                <p className="cp-serif cp-hero-text italic text-base sm:text-lg md:text-xl font-light text-cp-ivoire/95 leading-[1.5] mt-5 md:mt-7 max-w-xl">
                                    {current.lead}
                                </p>
                                <div className="mt-8 md:mt-10 flex items-center gap-4 md:gap-6 flex-wrap">
                                    <Link
                                        to={current.cta.to}
                                        className="cp-btn-outline-light"
                                        data-testid={`hero-slide-cta-${current.id}`}
                                    >
                                        {current.cta.label}
                                    </Link>
                                    <Link to="/carte" className="cp-link cp-link--light cp-tiny">
                                        ou voir la carte →
                                    </Link>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>

            {/* Slide selector — bottom left */}
            <div className="absolute bottom-6 md:bottom-10 left-5 sm:left-6 md:left-12 z-20 flex items-center gap-2 md:gap-3 pointer-events-auto" data-testid="hero-slide-nav">
                {SLIDES.map((s, i) => (
                    <button
                        key={s.id}
                        onClick={() => goTo(i)}
                        data-testid={`hero-slide-dot-${i}`}
                        aria-label={`Aller au chapitre ${i + 1}`}
                        className="group flex items-center gap-1.5 md:gap-2 transition-all"
                    >
                        <span className={`h-px transition-all ${selected === i ? "w-10 md:w-14 bg-cp-sable" : "w-5 md:w-8 bg-cp-ivoire/30 group-hover:bg-cp-ivoire/60"}`} />
                        <span className={`cp-tiny text-[9px] md:text-[10px] ${selected === i ? "text-cp-sable" : "text-cp-ivoire/40 group-hover:text-cp-ivoire/70"}`}>
                            {String(i + 1).padStart(2, "0")}
                        </span>
                    </button>
                ))}
            </div>

            {/* Prev/Next (desktop only) */}
            <div className="hidden md:flex absolute bottom-10 right-12 z-20 items-center gap-4 pointer-events-auto">
                <button onClick={prev} data-testid="hero-prev" aria-label="Précédent" className="cp-tiny text-cp-ivoire/60 hover:text-cp-ivoire transition-colors">← Préc.</button>
                <span className="w-px h-4 bg-cp-ivoire/20" />
                <button onClick={next} data-testid="hero-next" aria-label="Suivant" className="cp-tiny text-cp-ivoire/60 hover:text-cp-ivoire transition-colors">Suivant →</button>
            </div>

            {/* Brand seal — bottom right corner, engraved watermark feel */}
            <div className="hidden md:flex absolute bottom-28 right-12 z-10 flex-col items-end gap-2 opacity-70 pointer-events-none">
                <svg width="48" height="48" viewBox="0 0 120 120" fill="none">
                    <path
                        d="M 60 6 C 92 7 113 30 114 60 C 115 91 92 113 60 114 C 28 114 6 92 7 60 C 6 29 29 7 60 6 Z"
                        stroke="rgb(201,184,150)" strokeWidth="1.1" fill="none" opacity="0.85"
                    />
                    <text x="30" y="82" fontFamily="'Cormorant Garamond',serif" fontStyle="italic" fontWeight="500" fontSize="72" fill="rgb(201,184,150)" letterSpacing="-2">C</text>
                    <text x="56" y="94" fontFamily="'Cormorant Garamond',serif" fontStyle="italic" fontWeight="500" fontSize="72" fill="rgb(201,184,150)" letterSpacing="-2">P</text>
                </svg>
                <span className="cp-tiny text-cp-sable/70" style={{ fontSize: "9px", letterSpacing: "0.3em" }}>EST · MMXXVI</span>
            </div>

            {/* Scroll cue */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 hidden lg:block">
                <span className="cp-vertical text-cp-ivoire/40">Faites défiler</span>
            </div>
        </section>
    );
}
